import React, { useMemo, useRef } from 'react';
import type { Chart as ChartJS, TooltipItem } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import KpiChartPanel from './KpiChartPanel';
import { chartElementAtEvent } from '../chartInteraction';
import { formatAxisCompactK } from '../chartFormatters';
import type { SlaViewBucketKey, SlaViewTypeDistributionDto } from '../kpiDashboardApi';
import {
  KPI_GREEN,
  KPI_LIGHT_BLUE,
  KPI_ORANGE,
  KPI_PRIMARY_BLUE,
} from './kpiChartPalette';

const BUCKET_COLORS: Record<SlaViewBucketKey, string> = {
  '0-24': KPI_GREEN,
  '25-49': KPI_LIGHT_BLUE,
  '50-74': KPI_PRIMARY_BLUE,
  '75-99': KPI_ORANGE,
  breached: '#dc2626',
};

export interface KpiSlaBucketBarChartProps {
  distribution: SlaViewTypeDistributionDto;
  selectedBucket: SlaViewBucketKey | null;
  onBucketClick: (bucket: SlaViewBucketKey) => void;
  isLoading?: boolean;
}

/**
 * SLA View bucket bar chart — same chrome as {@link KpiChangeLabeledBarChart}.
 */
const KpiSlaBucketBarChart: React.FC<KpiSlaBucketBarChartProps> = ({
  distribution,
  selectedBucket,
  onBucketClick,
  isLoading,
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const rows = distribution.buckets;
  const total = distribution.total;

  const labels = useMemo(() => rows.map((r) => r.label), [rows]);
  const values = useMemo(() => rows.map((r) => r.count), [rows]);
  const maxV = Math.max(0, ...values, 1);

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const idx = el.index;
    if (idx == null || idx < 0 || idx >= rows.length) return;
    const bucket = rows[idx];
    if (!bucket || bucket.count <= 0) return;
    onBucketClick(bucket.key);
  };

  if (isLoading) {
    return (
      <KpiChartPanel title={distribution.label} className="h-full" flexFill titleClassName="text-center">
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      </KpiChartPanel>
    );
  }

  if (!total) {
    return (
      <KpiChartPanel title={distribution.label} className="h-full" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          No SLA records for the current filters.
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={`${distribution.label} (${total.toLocaleString()})`}
      className="h-full"
      flexFill
      titleClassName="text-center"
    >
      <div className="relative min-h-[260px] w-full flex-1 px-1 pb-1">
        <Chart
          ref={chartRef}
          type="bar"
          onClick={handleClick}
          data={{
            labels,
            datasets: [
              {
                label: 'Tickets',
                data: values,
                backgroundColor: rows.map((r) =>
                  r.key === selectedBucket
                    ? BUCKET_COLORS[r.key]
                    : `${BUCKET_COLORS[r.key]}cc`
                ),
                borderColor: rows.map((r) =>
                  r.key === selectedBucket ? '#312e81' : BUCKET_COLORS[r.key]
                ),
                borderWidth: rows.map((r) => (r.key === selectedBucket ? 2 : 0)),
                borderRadius: 6,
                barThickness: 36,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: '#111827',
                  font: { weight: 600, size: 10 },
                  formatter: (_value: number, ctx: DataLabelContext) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    return v > 0 ? String(v) : '';
                  },
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 22, right: 8, left: 4, bottom: 4 } },
            onHover: (event, elements) => {
              const canvas = event.native?.target;
              if (!(canvas instanceof HTMLElement)) return;
              const el = elements[0];
              const idx = el?.index;
              const v = idx !== undefined ? values[idx] : undefined;
              canvas.style.cursor = v != null && v > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    const n = Number(ctx.raw) || 0;
                    const pct = total > 0 ? ((n / total) * 100).toFixed(1) : '0.0';
                    return ` ${ctx.label}: ${n.toLocaleString()} (${pct}%)`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 40 },
              },
              y: {
                beginAtZero: true,
                suggestedMax: maxV * 1.15,
                ticks: {
                  color: '#6b7280',
                  font: { size: 10 },
                  precision: 0,
                  callback: (v) => formatAxisCompactK(Number(v)),
                },
                grid: { color: 'rgba(148,163,184,0.25)' },
              },
            },
          }}
        />
      </div>
      <p className="pb-2 text-center text-[10px] text-slate-500">Click a bar to view tickets</p>
    </KpiChartPanel>
  );
};

export default KpiSlaBucketBarChart;
